import React, { useState, useEffect, useMemo } from 'react';
import { Box, Text } from 'grommet';
import TagSelector from '../tag-selector';
import { authorizedFetch } from '../../utils';
import { debounce } from 'lodash';

export const getGeoNameEntries = (
  geoNameIds,
  setEntries,
  addError = () => {}
) => {
  let body = {
    geoNameIds
  };

  authorizedFetch(`/api/location-api/lookup`, 'POST', body)
    .then(response => {
      const nonNullEntries = response.reduce((pv, cv) => {
        if (!cv) return pv;
        pv[cv.geoNameId] = cv;
        return pv;
      }, {});
      setEntries(nonNullEntries);
    })
    .catch(e => addError('Error lookup GeoName data', e.message));
};

const getLocationSuggestions = debounce(
  (searchText, existingLocationIds, setLocationSuggestions, addError) => {
    authorizedFetch(
      `/api/location-api/search/places?query=${searchText}`,
      'GET'
    )
      .then(response => {
        const filteredSuggestions = response.filter(r => existingLocationIds.indexOf(r.geoNameId.toString()) === -1);
        setLocationSuggestions(filteredSuggestions);
      })
      .catch(e => addError('Unable to get search suggestions', e.message));
  },
  150
);

const getItemDetailLabel = ({ countryCode, type, stateCode }) => {
  let detail = `${type} - ${countryCode}`;
  if (stateCode) {
    detail += ` - ${stateCode}`;
  }

  return detail;
};

const getShortItemDetailLabel = ({ name, countryCode, type, stateCode }) => {
  let detail = name;

  if (type === 'State') {
    detail += `, ${countryCode}`;
    return detail;
  }

  if(type === 'City') {
    detail += `, ${stateCode}`
  }
  

  return detail;
};

const getSuggestionItem = item => {
  const { name, population } = item;

  let populationLabel;
  if (population) {
    populationLabel = `Population: ${population}`;
  }

  const detail = getItemDetailLabel(item);

  return {
    label: (
      <Box
        pad='small'
        direction='row'
        gap='small'
        align='center'
        justify='between'
        flex={{ shrink: 0 }}
      >
        <Text title={name} truncate>
          {name}
        </Text>
        <Box direction='column' flex={{ shrink: 0 }} align='end'>
          <Text title={item.name} size='small' color='dark-5' truncate>
            {detail}
          </Text>
          <Text title={populationLabel} size='small' color='dark-5' truncate>
            {populationLabel}
          </Text>
        </Box>
      </Box>
    ),
    value: item
  };
};

const LocationSelector = ({ locationIds, setLocationIds, addError }) => {
  const [geoNameEntries, setGeoNameEntries] = useState({});
  const [suggestions, setSuggestions] = useState([]);
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    if (locationIds.length === 0) return;

    const locationsToQuery = locationIds.reduce((pv, cv) => {
      if (geoNameEntries[cv]) return pv;
      return [...pv, cv];
    }, []);
    if (locationsToQuery.length === 0) return;

    getGeoNameEntries(locationsToQuery, setGeoNameEntries);
  }, []);

  useEffect(() => {
    if (searchText === '') {
      setSuggestions([]);
      return;
    }

    getLocationSuggestions(searchText, locationIds, setSuggestions, addError);
  }, [searchText, locationIds, addError]);

  const memoizedSuggestions = useMemo(() => {
    let renderedSuggestions = suggestions.map(h => getSuggestionItem(h));
    const searchTextAsGeoNameId = Number.parseInt(searchText);
    if (searchTextAsGeoNameId) {
      renderedSuggestions.push({
        label: (
          <Box
            pad='small'
            direction='row'
            gap='small'
            align='center'
            justify='between'
            flex={{ shrink: 0 }}
            style={{ minWidth: '400px' }}
          >
            <Text title={searchText} truncate>
              {searchText}
            </Text>

            <Text size='small' color='dark-5' truncate>
              GeoNameId
            </Text>
          </Box>
        ),
        value: {
          name: searchText,
          geoNameId: searchTextAsGeoNameId
        }
      });
    }

    return renderedSuggestions;
  }, [suggestions, searchText]);

  return (
    <Box flex={{ grow: 1 }}>
      <TagSelector
        width='auto'
        canCreateNew={false}
        placeholder='Add Location'
        suggestions={memoizedSuggestions}
        onTextChange={setSearchText}
        tagTextBuilder={geoNameId => {
          if (geoNameEntries[geoNameId]) {
            return getShortItemDetailLabel(geoNameEntries[geoNameId]);
          }
          return geoNameId;
        }}
        tags={locationIds}
        onTagAdd={suggestion => {
          const { geoNameId } = suggestion.value;

          const newLocationIds = [...locationIds, geoNameId];
          setLocationIds(newLocationIds);

          if (!geoNameEntries[geoNameId]) {
            const newGeoNameEntries = {
              ...geoNameEntries,
              [geoNameId]: { ...suggestion.value }
            };
            setGeoNameEntries(newGeoNameEntries);
          }
        }}
        onTagRemove={geoNameId => {
          const newLocationIds = [...locationIds.filter(x => x !== geoNameId)];
          setLocationIds(newLocationIds);
        }}
      />
    </Box>
  );
};

LocationSelector.displayName = 'LocationSelector';

export default LocationSelector;
